import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import cloneDeep from "lodash/cloneDeep";

import DataGrid from "@viz-ui/components/dataGrid/dataGrid";
import RecordProcessPanel from "@viz-ui/components/RecordProcessPanel/RecordProcessPanel";
import DataTableConfig from "@viz-ui/components/DataTableConfig/DataTableConfig";
import DownloadReportPanel from "@viz-ui/components/DownloadReportPanel/DownloadReportPanel";

import { useDataGridProps } from "./hooks/useDataGridProps";
import { useProcessPanelProps } from "./hooks/useProcessPanelProps";

import tableCapability from "@visualizer/modules/core/tableCapability/tableCapability.service";
import quickMenuToggler from "@viz-ui/services/quickMenu/quickMenuTogglerService";
import "./DataTablePanel.scss";

const getRowId = (row, rowIdKey) => {
  const rowId = row[rowIdKey];
  return rowId && typeof rowId === "object" ? rowId.value : rowId;
};

const DataTablePanel = ({
  commentCounts,
  data,
  fields,
  fieldFormatIdentifier,
  filteredRecordCount,
  tabIndex,

  onAddComment: onAddCommentParent,
  onScrollToBottom: onScrollToBottomParent,
  onDeleteRows,
  enableAllButtons,
  disableAllButtons,
  onBulkUpdate,
  onDeleteInProgress: onDeleteInProgressParent,
  onProcessRecord: onProcessRecordParent,
  onReload,

  appConfig,
  dataModel,
  eventService,
  dataFilter,
  localize,
  tabStateService,
  columnConfigClass,
  fieldClass,
  processPanelService,
}) => {
  if (!appConfig || !dataModel || !eventService || !dataFilter || !localize) {
    console.error("DataTablePanel: Missing required services", {
      appConfig: !!appConfig,
      dataModel: !!dataModel,
      eventService: !!eventService,
      dataFilter: !!dataFilter,
      localize: !!localize,
      tabStateService: !!tabStateService,
      columnConfigClass: !!columnConfigClass,
      fieldClass: !!fieldClass,
      processPanelService: !!processPanelService,
    });
    return (
      <div className="data-panel visualizer__table-colset">Error: Missing required services for DataTablePanel</div>
    );
  }

  const [checkedRecordIds, setCheckedRecordIds] = useState([]);
  const [checkedRecordCount, setCheckedRecordCount] = useState(0);
  const [rowCheckedInverse, setRowCheckedInverse] = useState(false);
  const [bulkModeEnabled, setBulkModeEnabled] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(undefined);
  const [selectedRecordData, setSelectedRecordData] = useState(undefined);
  const [showProcessPanel, setShowProcessPanel] = useState(false);
  const [showTableConfig, setShowTableConfig] = useState(false);
  const [isReportDownloadPanelOpen, setIsReportDownloadPanelOpen] = useState(false);
  const [isFirstRecord, setIsFirstRecord] = useState(false);
  const [isLastRecord, setIsLastRecord] = useState(false);
  const [dataGridData, setDataGridData] = useState([]);
  const [fieldModels, setFieldModels] = useState([]);
  const [tableConfigFieldObjs, setTableConfigFieldObjs] = useState([]);
  const [filterFieldNames, setFilterFieldNames] = useState([]);
  const [sortFieldName, setSortFieldName] = useState(undefined);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [selectedFieldName, setSelectedFieldName] = useState(undefined);
  const [columnConfigs, setColumnConfigs] = useState([]);
  const [configFields, setConfigFields] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [openStatuses, setOpenStatuses] = useState([]);
  const [conditionalFormattingVersion, setConditionalFormattingVersion] = useState(0);
  const tableNeedsRefreshRef = useRef(false);
  const tableNeedsHardRefreshRef = useRef(false);
  const dataGridRef = useRef(null);
  const tabStateRef = useRef(null);
  const isInitialDataLoadRef = useRef(true);
  const registeredEventServiceRef = useRef(null);
  const pendingOperationsRef = useRef(null);

  const tableCanRemediateRecords = useCallback(() => {
    return tableCapability.canRemediateRecords();
  }, []);

  const getFixedColumnsLeft = useCallback(() => {
    if (appConfig && appConfig.features && appConfig.features.fixedColumnLeftInViz) {
      return tableCanRemediateRecords() ? 2 : 1;
    }
    return 0;
  }, [appConfig, tableCanRemediateRecords]);

  const getAttachmentUrl = useCallback(() => {
    return appConfig && appConfig.getAttachmentUrl;
  }, [appConfig]);

  const findRowIndexById = useCallback((id, rowData) => {
    const rowIdKey = "metadata.exception_id";
    return (rowData || []).findIndex(row => getRowId(row, rowIdKey) === id);
  }, []);

  const updateCheckedRecordCount = useCallback(() => {
    let count = 0;
    if (checkedRecordIds) {
      if (rowCheckedInverse) {
        count = (filteredRecordCount || 0) - checkedRecordIds.length;
      } else {
        count = checkedRecordIds.length;
      }
    }
    setCheckedRecordCount(count);
  }, [checkedRecordIds, filteredRecordCount, rowCheckedInverse]);

  const updateIsFirstOrLastRecord = useCallback(
    newRowIndex => {
      if (!data) {
        setIsFirstRecord(true);
        setIsLastRecord(true);
      } else {
        setIsFirstRecord(newRowIndex === 0);
        setIsLastRecord(newRowIndex === data.length - 1);
      }
    },
    [data]
  );

  const fieldObjToFieldModel = useCallback(
    (fieldName, fieldObj) => {
      if (!fieldClass) return null;
      return new fieldClass()
        .name(fieldName)
        .displayName(fieldObj.displayName)
        .type(fieldObj.type)
        .isPrimaryKey(fieldObj.isPrimaryKey);
    },
    [fieldClass]
  );

  const fieldObjsToFieldModels = useCallback(
    fieldObjs => {
      const models = [];
      for (const fieldName in fieldObjs) {
        if (Object.prototype.hasOwnProperty.call(fieldObjs, fieldName)) {
          const model = fieldObjToFieldModel(fieldName, fieldObjs[fieldName]);
          if (model) models.push(model);
        }
      }
      return models;
    },
    [fieldObjToFieldModel]
  );

  const columnConfigObjToColumnConfigModel = useCallback(
    columnConfigObj => {
      if (!columnConfigClass) return null;
      const model = new columnConfigClass()
        .name(columnConfigObj.fieldName)
        .visible(columnConfigObj.visible !== false)
        .wordWrap(!!columnConfigObj.wordWrap);
      if (typeof columnConfigObj.columnWidth !== "undefined") {
        model.width(columnConfigObj.columnWidth);
      }
      return model;
    },
    [columnConfigClass]
  );

  const columnConfigModelToColumnConfigObj = useCallback(columnConfigModel => {
    const obj = {
      fieldName: columnConfigModel.name(),
      visible: columnConfigModel.visible(),
      wordWrap: columnConfigModel.wordWrap(),
    };
    if (typeof columnConfigModel.width() !== "undefined") {
      obj.columnWidth = columnConfigModel.width();
    }
    return obj;
  }, []);

  const updateFieldsModels = useCallback(() => {
    if (!dataModel) return;

    const tableFields = dataModel.table.fields();
    const newFieldModels = fieldObjsToFieldModels(tableFields);
    const newColumnConfigObjs = dataModel.tableConfig.columns();

    setFieldModels(newFieldModels);
    setConfigFields(newFieldModels);
    setTableConfigFieldObjs(newColumnConfigObjs);
    setColumnConfigs(newColumnConfigObjs.map(columnConfigObjToColumnConfigModel).filter(Boolean));
  }, [dataModel, fieldObjsToFieldModels, columnConfigObjToColumnConfigModel]);

  const updateDataGridData = useCallback(() => {
    if (!dataModel || !dataFilter) return;

    const filterConfig = dataModel.getFilterConfig();
    const newFilterFieldNames = dataFilter.getFilterFieldNames(filterConfig);

    let newSortFieldName;
    let newSortOrder;

    if (filterConfig.sortField) {
      newSortFieldName = filterConfig.sortField.field;
      newSortOrder = filterConfig.sortField.order;
    }

    setFilterFieldNames(newFilterFieldNames);
    setSortFieldName(newSortFieldName);
    setSortOrder(newSortOrder);
    setDataGridData(data || []);
  }, [dataModel, dataFilter, data]);

  const closeProcessPanel = useCallback(() => {
    setShowProcessPanel(false);
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("processPanel.close");
    }
  }, []);

  const openProcessPanel = useCallback(() => {
    setShowProcessPanel(true);
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("processPanel.open");
      registeredEventServiceRef.current.publish("quickMenu.close");
      registeredEventServiceRef.current.publish("filterPanel.close");
    }
    setShowTableConfig(false);
    setIsReportDownloadPanelOpen(false);
  }, []);

  const closeConfigPanel = useCallback(() => {
    setShowTableConfig(false);
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("dataTableConfigPanel.close");
    }
  }, []);

  const toggleConfigPanel = useCallback(() => {
    const newState = !showTableConfig;
    setShowTableConfig(newState);

    if (newState) {
      if (showProcessPanel) closeProcessPanel();
      if (isReportDownloadPanelOpen) setIsReportDownloadPanelOpen(false);
    }
  }, [showTableConfig, showProcessPanel, isReportDownloadPanelOpen, closeProcessPanel]);

  const resetCheckedRows = useCallback(() => {
    setCheckedRecordIds([]);
    setRowCheckedInverse(false);
    setCheckedRecordCount(0);
  }, []);

  const uncheckRows = useCallback(rows => {
    setCheckedRecordIds(prev => {
      const newIds = prev.filter(id => !rows.includes(id));
      return newIds;
    });
  }, []);

  const checkRowCheckbox = useCallback(rowId => {
    const checkbox = document.querySelector(
      `.ht_clone_left table td[data-record-id="${rowId}"] input.htCheckboxRendererInput`
    );

    if (checkbox) {
      checkbox.click();
    } else {
      setCheckedRecordIds(prev => {
        const index = prev.indexOf(rowId);
        if (index < 0) {
          return [...prev, rowId];
        } else {
          return prev.filter((_, i) => i !== index);
        }
      });
    }
  }, []);

  const incrementSelectedRowBy = useCallback(
    increment => {
      if (!dataGridData.length) return;

      const rowIdKey = "metadata.exception_id";
      const currentRowIndex = findRowIndexById(selectedRecordId, dataGridData);
      const newRowIndex = currentRowIndex + increment;

      if (newRowIndex >= 0 && newRowIndex < dataGridData.length) {
        const newRowId = dataGridData[newRowIndex][rowIdKey];
        const newRowData = dataGridData[newRowIndex];

        setSelectedRecordId(newRowId);
        setSelectedRecordData(newRowData);

        if (newRowIndex === dataGridData.length - 1) {
          if (onScrollToBottomParent) {
            onScrollToBottomParent();
          }
        }

        updateIsFirstOrLastRecord(newRowIndex);
      }
    },
    [dataGridData, selectedRecordId, findRowIndexById, updateIsFirstOrLastRecord, onScrollToBottomParent]
  );

  const handleColumnHeaderClick = useCallback(
    ({ fieldName, offset }) => {
      if (!bulkModeEnabled) {
        closeProcessPanel();
        quickMenuToggler.toggle(offset, fieldName, true);
      }
    },
    [bulkModeEnabled, closeProcessPanel]
  );

  const handleColumnResize = useCallback(
    ({ columnIndex, fieldName, width }) => {
      if (!dataModel) return;

      const columns = cloneDeep(dataModel.getTableConfigObj().columns);
      const column = columns.find(c => c.fieldName === fieldName);
      if (column) {
        column.columnWidth = width;
        dataModel.tableConfig.columns(columns);
        if (registeredEventServiceRef.current) {
          registeredEventServiceRef.current.publish("dataTable.columnResized", column);
        }
        setTableConfigFieldObjs(columns);
      }
    },
    [dataModel]
  );

  const handleRowClick = useCallback(
    ({ row, rowId }) => {
      const rowIndex = findRowIndexById(rowId, dataGridData);
      const rowData = dataGridData[rowIndex];

      updateIsFirstOrLastRecord(rowIndex);
      setSelectedRecordData(rowData);
      setSelectedRecordId(rowId);
      openProcessPanel();
    },
    [dataGridData, findRowIndexById, updateIsFirstOrLastRecord, openProcessPanel]
  );

  const handleScrollToBottom = useCallback(() => {
    if (onScrollToBottomParent) {
      onScrollToBottomParent();
    }
  }, [onScrollToBottomParent]);

  const handleSelectionChange = useCallback(
    ({ checkedRowIds: newCheckedRowIds, selectedRowId: newSelectedRowId, inverseMode }) => {
      // 1. Update state IMMEDIATELY - React will batch these and render checkboxes
      setCheckedRecordIds([...newCheckedRowIds]);
      setSelectedRecordId(newSelectedRowId);
      setRowCheckedInverse(inverseMode);

      // 2. CANCEL any pending heavy operations (debounce)
      if (pendingOperationsRef.current) {
        clearTimeout(pendingOperationsRef.current);
      }

      // 3. DEFER heavy operations until AFTER React renders AND user stops clicking
      // Using setTimeout with 50ms delay for broad browser compatibility
      pendingOperationsRef.current = setTimeout(() => {
        try {
          // Only redraw ONCE after all selections are done
          if (dataGridRef.current && dataGridRef.current.redraw) {
            dataGridRef.current.redraw();
          }

          // Only find and open panel if selection state actually changed
          if (newCheckedRowIds.length > 0 || inverseMode) {
            if (!showProcessPanel) {  // Only if not already open
              const rowIdKey = "metadata.exception_id";
              const selectedRowData = dataGridData.find(row => {
                const actualId = getRowId(row, rowIdKey);
                return actualId === newSelectedRowId;
              });

              if (selectedRowData) {
                const rowIndex = dataGridData.indexOf(selectedRowData);
                updateIsFirstOrLastRecord(rowIndex);
                setSelectedRecordData(selectedRowData);
                openProcessPanel();
              }
            }
          }
        } finally {
          pendingOperationsRef.current = null;
        }
      }, 50);
    },
    [dataGridData, updateIsFirstOrLastRecord, openProcessPanel, showProcessPanel]
  );

  const handleAddComment = useCallback(
    ({ rowId, hasComment, hasAttachment }) => {
      const newCommentCounts = commentCounts ? [...commentCounts] : [];
      const index = newCommentCounts.findIndex(record => record.id === rowId);
      const commentIncrement = hasComment ? 1 : 0;
      const attachmentIncrement = hasAttachment ? 1 : 0;

      if (index >= 0) {
        newCommentCounts[index] = {
          ...newCommentCounts[index],
          comments_count: newCommentCounts[index].comments_count + commentIncrement,
          attachments_count: newCommentCounts[index].attachments_count + attachmentIncrement,
        };
      } else {
        newCommentCounts.push({
          id: rowId,
          comments_count: commentIncrement,
          attachments_count: attachmentIncrement,
        });
      }

      if (onAddCommentParent) {
        onAddCommentParent({ commentCounts: newCommentCounts });
      }
    },
    [commentCounts, onAddCommentParent]
  );

  const handleDeleteRecord = useCallback(
    ({ rowId }) => {
      if (onDeleteRows) {
        onDeleteRows({ recordIds: [rowId], mode: "deleteOne" });
      }
      incrementSelectedRowBy(1);
      resetCheckedRows();
    },
    [onDeleteRows, incrementSelectedRowBy, resetCheckedRows]
  );

  const handleDeleteInProgress = useCallback(() => {
    if (onDeleteInProgressParent) {
      onDeleteInProgressParent({
        message: localize ? localize.getLocalizedString("_BulkUpdate.DeleteInProgress.Message_") : "",
      });
    }
  }, [onDeleteInProgressParent, localize]);

  const handleDeleteBulk = useCallback(
    ({ deleted, notDeleted }) => {
      tableNeedsRefreshRef.current = true;
      tableNeedsHardRefreshRef.current = true;

      const successfulCount = deleted.length;
      const unsuccessfulCount = notDeleted.length;
      const tableRefresh = unsuccessfulCount === 0;

      let message;
      let messageType;

      if (tableRefresh) {
        message = localize ? localize.getLocalizedString("_BulkUpdate.DeleteSuccess.Message_") : "";
        messageType = "success";
        resetCheckedRows();
        closeProcessPanel();
        setBulkModeEnabled(false);
        if (enableAllButtons) {
          enableAllButtons();
        }
      } else {
        setCheckedRecordIds(prev => {
          if (rowCheckedInverse) {
            return [...prev, ...deleted];
          } else {
            return prev.filter(id => !deleted.includes(id));
          }
        });

        message = localize
          ? localize.getLocalizedStringWithTokenReplacement("_BulkUpdate.DeletePartial.Message_", {
              successfulCount,
              unsuccessfulCount,
            })
          : "";
        messageType = "warning";
      }

      if (onBulkUpdate) {
        onBulkUpdate({
          tableRefresh,
          message,
          messageType,
          hardTableRefresh: tableNeedsHardRefreshRef.current,
          isDeletion: true,
        });
      }
    },
    [localize, resetCheckedRows, closeProcessPanel, enableAllButtons, onBulkUpdate, rowCheckedInverse]
  );

  const handleProcessRecord = useCallback(
    ({ processed, unprocessed, newFieldData, removeFromView }) => {
      const rowIdKey = "metadata.exception_id";

      if (removeFromView && removeFromView.length > 0) {
        uncheckRows(removeFromView);
        if (removeFromView.includes(selectedRecordId)) {
          closeProcessPanel();
        }
      }

      // Show success toast for single record processing
      if (processed && processed.length > 0 && onBulkUpdate) {
        onBulkUpdate({
          tableRefresh: false,
          message: localize
            ? localize.getLocalizedString("_BulkUpdate.Success.Message_")
            : "Record(s) updated successfully",
          messageType: "success",
          hardTableRefresh: false,
        });
      }

      if (onProcessRecordParent) {
        onProcessRecordParent({ processed, rowIdKey, newFieldData, removeFromView });
      }
    },
    [selectedRecordId, uncheckRows, closeProcessPanel, onProcessRecordParent, onBulkUpdate, localize]
  );

  const handleProcessBulk = useCallback(
    ({ requireDelayJob, processed, unprocessed, fromQuestionnaire, removeFromView }) => {
      if (fromQuestionnaire) {
        tableNeedsRefreshRef.current = true;
        tableNeedsHardRefreshRef.current = true;
      }

      if (requireDelayJob) {
        if (onBulkUpdate) {
          onBulkUpdate({
            tableRefresh: tableNeedsRefreshRef.current,
            message: localize ? localize.getLocalizedString("_BulkUpdate.DelayedJob.Message_") : "",
            messageType: "warning",
            hardTableRefresh: tableNeedsHardRefreshRef.current,
          });
        }

        resetCheckedRows();
        closeProcessPanel();
        setBulkModeEnabled(false);
        if (enableAllButtons) {
          enableAllButtons();
        }
      } else {
        const successfulCount = processed.length;
        const unsuccessfulCount = unprocessed.length;
        const tableRefresh = unsuccessfulCount === 0;

        let message;
        let messageType;

        if (tableRefresh) {
          message = localize ? localize.getLocalizedString("_BulkUpdate.Success.Message_") : "";
          messageType = "success";
          resetCheckedRows();
          closeProcessPanel();
          setBulkModeEnabled(false);
          if (enableAllButtons) {
            enableAllButtons();
          }
        } else {
          setCheckedRecordIds(prev => {
            if (rowCheckedInverse) {
              return [...prev, ...processed];
            } else {
              return prev.filter(id => !processed.includes(id));
            }
          });

          const messageKey = fromQuestionnaire ? "_BulkQuestionnaire.Partial.Message_" : "_BulkUpdate.Partial.Message_";
          message = localize
            ? localize.getLocalizedStringWithTokenReplacement(messageKey, {
                successfulCount,
                unsuccessfulCount,
              })
            : "";
          messageType = "warning";
          tableNeedsRefreshRef.current = true;
        }

        if (onBulkUpdate) {
          onBulkUpdate({
            tableRefresh,
            message,
            messageType,
            hardTableRefresh: tableNeedsHardRefreshRef.current,
          });
        }
      }
    },
    [localize, resetCheckedRows, closeProcessPanel, enableAllButtons, onBulkUpdate, rowCheckedInverse]
  );

  const handleEnterBulkReviewMode = useCallback(() => {
    if (disableAllButtons) {
      disableAllButtons();
    }
    setBulkModeEnabled(true);
  }, [disableAllButtons]);

  const handleExitBulkReviewMode = useCallback(() => {
    if (tableNeedsRefreshRef.current) {
      if (onBulkUpdate) {
        onBulkUpdate({
          tableRefresh: true,
          hardTableRefresh: tableNeedsHardRefreshRef.current,
        });
      }
      resetCheckedRows();
    }

    if (enableAllButtons) {
      enableAllButtons();
    }
    setBulkModeEnabled(false);
    tableNeedsRefreshRef.current = false;
    tableNeedsHardRefreshRef.current = false;
  }, [enableAllButtons, resetCheckedRows, onBulkUpdate]);

  const handleClosePanel = useCallback(() => {
    closeProcessPanel();
    if (bulkModeEnabled) {
      handleExitBulkReviewMode();
    }
  }, [closeProcessPanel, bulkModeEnabled, handleExitBulkReviewMode]);

  const handleDataTableConfigSubmit = useCallback(
    ({ columnConfigs: newColumnConfigModels }) => {
      if (!dataModel) return;
      const columnConfigObjs = newColumnConfigModels.map(columnConfigModelToColumnConfigObj);
      dataModel.tableConfig.columns(columnConfigObjs);
      setTableConfigFieldObjs(columnConfigObjs);
      setColumnConfigs(columnConfigObjs.map(columnConfigObjToColumnConfigModel).filter(Boolean));
    },
    [dataModel, columnConfigModelToColumnConfigObj, columnConfigObjToColumnConfigModel]
  );

  const handleDataTableConfigCancel = useCallback(() => {
    closeConfigPanel();
  }, [closeConfigPanel]);

  const handleDataTableConfigClose = useCallback(() => {
    closeConfigPanel();
  }, [closeConfigPanel]);

  const handleToggleReportDownloadPanel = useCallback(() => {
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("reportDownloadPanel.toggle.fromDataPanel");
    }
    setIsReportDownloadPanelOpen(prev => !prev);
  }, []);

  const handleGenerateReportDownload = useCallback((targetReport, filterExpression) => {
    setIsReportDownloadPanelOpen(false);
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("report.download", { targetReport, filterExpression });
    }
  }, []);

  const handleEmailReport = useCallback((targetReport, filterExpression) => {
    setIsReportDownloadPanelOpen(false);
    if (registeredEventServiceRef.current) {
      registeredEventServiceRef.current.publish("report.email", { targetReport, filterExpression });
    }
  }, []);

  const handleTabStateReload = useCallback(
    triggeredFrom => {
      const doLoadData = triggeredFrom !== "conditionalFormatting";
      const doNotRenderActions = ["columnSort", "filterChange", "selectedTab"];
      const doRenderGrid = !doNotRenderActions.includes(triggeredFrom);

      if (doLoadData && onReload) {
        onReload();
      }

      if (tabStateRef.current) {
        tabStateRef.current.reloaded(doRenderGrid);
      }

      resetCheckedRows();
      closeProcessPanel();
    },
    [onReload, resetCheckedRows, closeProcessPanel]
  );

  const handleTabStateRedraw = useCallback(() => {
    if (dataGridRef.current && dataGridRef.current.redraw) {
      dataGridRef.current.redraw();
    }
    if (tabStateRef.current) {
      tabStateRef.current.redrew();
    }
  }, []);

  useEffect(() => {
    if (!tabStateService) {
      console.warn("DataTablePanel: tabStateService not provided, skipping TabState integration");
      return;
    }

    const getTabIndex = () => Number(tabIndex);

    tabStateRef.current = tabStateService.instance(getTabIndex, handleTabStateReload, handleTabStateRedraw);

    return () => {
      tabStateRef.current = null;
    };
  }, [tabStateService, tabIndex, handleTabStateReload, handleTabStateRedraw]);

  useEffect(() => {
    updateCheckedRecordCount();
  }, [checkedRecordIds, rowCheckedInverse, filteredRecordCount, updateCheckedRecordCount]);

  useEffect(() => {
    if (data && fields && typeof filteredRecordCount !== "undefined") {
      updateFieldsModels();
      updateDataGridData();
      if (isInitialDataLoadRef.current && tabStateRef.current) {
        isInitialDataLoadRef.current = false;
        tabStateRef.current.scheduleRedraw();
      }
    }
  }, [data, fields, filteredRecordCount, updateFieldsModels, updateDataGridData]);

  useEffect(() => {
    if (selectedRecordData && selectedRecordId && data) {
      const rowIndex = findRowIndexById(selectedRecordId, data);
      if (rowIndex >= 0) {
        setSelectedRecordData(data[rowIndex]);
      } else {
        closeProcessPanel();
      }
    }
  }, [data, selectedRecordId, selectedRecordData, findRowIndexById, closeProcessPanel]);

  useEffect(() => {
    if (!dataModel || !dataModel.subscribe) return;

    const unsubscribe = dataModel.subscribe("filtersOpen", () => {
      const isFilterPanelOpen = dataModel.filtersOpen && dataModel.filtersOpen();
      if (isFilterPanelOpen) {
        closeProcessPanel();
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [dataModel, closeProcessPanel]);

  useEffect(() => {
    if (!dataModel || !dataModel.subscribe) return;

    const unsubscribe = dataModel.subscribe("tableConfig.columns", () => {
      const newColumnConfigObjs = dataModel.tableConfig.columns();
      setTableConfigFieldObjs(newColumnConfigObjs);
      setColumnConfigs(newColumnConfigObjs.map(columnConfigObjToColumnConfigModel).filter(Boolean));
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [dataModel, columnConfigObjToColumnConfigModel]);

  useEffect(() => {
    if (!eventService) return;

    const registeredService = eventService.register("dataTable.DataTablePanelController.React");
    registeredEventServiceRef.current = registeredService;

    registeredService.subscribe("dataTableConfigPanelExternal.close", () => {
      if (showTableConfig) toggleConfigPanel();
    });
    registeredService.subscribe("dataTableConfigPanelExternal.open", () => {
      if (!showTableConfig) toggleConfigPanel();
    });
    registeredService.subscribe("copyInterpretationsPanel.open", () => {
      if (showTableConfig) toggleConfigPanel();
      closeProcessPanel();
    });
    registeredService.subscribe("dataTable.resetCheckedRows", () => {
      resetCheckedRows();
    });
    registeredService.subscribe("biView.columnSelected", (event, fieldName) => {
      setSelectedFieldName(fieldName);
    });
    registeredService.subscribe("filterPanel.filterChange", () => {
      if (dataModel && dataFilter) {
        setFilterFieldNames(dataFilter.getFilterFieldNames(dataModel.getFilterConfig()));
      }
    });
    registeredService.subscribe("dataTable.configChange", (e, tableViewRedirect, triggeredFrom) => {
      if (tableViewRedirect && dataModel) {
        dataModel.interpretation.currentTabIndex(0);
      }
      // When conditional formatting changes, increment version to trigger re-computation of formatters
      if (triggeredFrom === "conditionalFormatting") {
        setConditionalFormattingVersion(prev => prev + 1);
      }
      if (tabStateRef.current) {
        tabStateRef.current.scheduleReload(false, triggeredFrom);
      }
    });
    registeredService.subscribe("sortUpdated", (event, fieldName, order) => {
      if (dataModel && dataFilter) {
        const filterConfig = dataModel.getFilterConfig();
        const isValid =
          dataModel.filtersValid && dataModel.filtersValid() && !dataFilter.hasInvalidFilters(filterConfig);
        if (isValid && fieldName && order) {
          dataFilter.setOrToggleSort(fieldName, order);
          if (tabStateService) {
            tabStateService.reloadAll(false, "columnSort");
          }
        }
      }
    });
    registeredService.subscribe("processPanel.force.close", () => {
      closeProcessPanel();
    });
    registeredService.subscribe(
      "reportDownloadPanel.toggle.fromDataVisualizer",
      (e, newReportTypes, newOpenStatuses) => {
        setReportTypes(newReportTypes);
        setOpenStatuses(newOpenStatuses);
        setIsReportDownloadPanelOpen(prev => !prev);
      }
    );

    return () => {
      registeredService.unregister();
      registeredEventServiceRef.current = null;
    };
  }, [
    eventService,
    dataModel,
    dataFilter,
    tabStateService,
    showTableConfig,
    toggleConfigPanel,
    closeProcessPanel,
    resetCheckedRows,
  ]);

  const dataGridProps = useDataGridProps({
    data: dataGridData,
    fields: fieldModels,
    fieldFormatIdentifier,
    tableConfigFieldObjs,
    commentCounts,
    checkedRecordIds,
    rowCheckedInverse,
    selectedRecordId,
    bulkModeEnabled,
    filterFieldNames,
    filteredRecordCount,
    fixedColumnsLeft: getFixedColumnsLeft(),
    getAttachmentUrl: getAttachmentUrl(),
    highlightSelectedRow: true,
    isHeaderClickingEnabled: true,
    isHeaderIconEnabled: true,
    isRowSelectionEnabled: tableCanRemediateRecords(),
    selectedFieldName,
    sortFieldName,
    sortOrder,
    showCommentCount: tableCanRemediateRecords(),
    appConfig,
    onColumnHeaderClick: handleColumnHeaderClick,
    onColumnResize: handleColumnResize,
    onRowClick: handleRowClick,
    onScrollToBottom: handleScrollToBottom,
    onSelectionChange: handleSelectionChange,
    conditionalFormattingVersion,
  });

  const processPanelProps = useProcessPanelProps({
    selectedRecordData,
    selectedRecordId,
    checkedRecordIds,
    checkedRecordCount,
    fieldModels,
    fieldFormatIdentifier,
    rowCheckedInverse,
    isFirstRecord,
    isLastRecord,
    isOpen: showProcessPanel,
    appConfig,
    dataModel,
    processPanelService,
    onProcessRecord: handleProcessRecord,
    onDeleteRecord: handleDeleteRecord,
    onAddComment: handleAddComment,
    onCheck: checkRowCheckbox,
    onClosePanel: handleClosePanel,
    onDeleteBulk: handleDeleteBulk,
    onDeleteInProgress: handleDeleteInProgress,
    onEnterBulkReviewMode: handleEnterBulkReviewMode,
    onExitBulkReviewMode: handleExitBulkReviewMode,
    onNextRowClick: () => incrementSelectedRowBy(1),
    onPrevRowClick: () => incrementSelectedRowBy(-1),
    onProcessBulk: handleProcessBulk,
  });

  return (
    <div className="data-panel visualizer__table-colset">
      {/* DataGrid Section */}
      <div className="visualizer__table-panel">
        <div className="visualizer__data-grid">
          <DataGrid ref={dataGridRef} {...dataGridProps} />
        </div>
      </div>

      {/* DataTableConfig Panel */}
      <div className={`visualizer__table-config-panel ${!showTableConfig ? "panel--closed" : ""}`}>
        <DataTableConfig
          columnConfigs={columnConfigs}
          fields={configFields}
          onSubmitClick={handleDataTableConfigSubmit}
          onCancelClick={handleDataTableConfigCancel}
          onCloseClick={handleDataTableConfigClose}
        />
      </div>

      {/* Process Panel - matches Angular's processPanel.tpl.html structure */}
      <div className={`visualizer__process-panel ${!showProcessPanel ? "panel--closed" : ""}`}>
        <div className="process-panel__main-container">
          <div className="viz-panel__header">
            <h2 tabIndex="-1" id="process-panel-title">
              {localize ? localize.getLocalizedString("_ProcessPanel.Headline.Label_") : "Process Records"}
            </h2>
            <button
              className="viz-panel__header-button"
              id="process-panel-close-button"
              onClick={handleClosePanel}
              aria-label={localize ? localize.getLocalizedString("_General.Close.Label_") : "Close"}
            >
              <i className="acl-i-times"></i>
              {localize ? localize.getLocalizedString("_General.Close.Label_") : "Close"}
            </button>
          </div>
          <div className="process-panel__body">
            {processPanelProps && <RecordProcessPanel {...processPanelProps} />}
          </div>
        </div>
      </div>

      {/* Download Report Panel */}
      {isReportDownloadPanelOpen && (
        <div>
          <DownloadReportPanel
            toggle={handleToggleReportDownloadPanel}
            isOpen={isReportDownloadPanelOpen}
            reportTypes={reportTypes}
            generateReportDownload={handleGenerateReportDownload}
            emailReport={handleEmailReport}
            vizDataModel={dataModel}
            checkedRowIds={checkedRecordIds}
            openStatuses={openStatuses}
          />
        </div>
      )}
    </div>
  );
};

DataTablePanel.propTypes = {
  commentCounts: PropTypes.array,
  data: PropTypes.array,
  fields: PropTypes.object,
  fieldFormatIdentifier: PropTypes.string,
  filteredRecordCount: PropTypes.number,
  tabIndex: PropTypes.number,
  onAddComment: PropTypes.func,
  onScrollToBottom: PropTypes.func,
  onDeleteRows: PropTypes.func,
  enableAllButtons: PropTypes.func,
  disableAllButtons: PropTypes.func,
  onBulkUpdate: PropTypes.func,
  onDeleteInProgress: PropTypes.func,
  onProcessRecord: PropTypes.func,
  onReload: PropTypes.func,
  appConfig: PropTypes.object,
  dataModel: PropTypes.object,
  eventService: PropTypes.object,
  dataFilter: PropTypes.object,
  localize: PropTypes.object,
  tabStateService: PropTypes.object,
  columnConfigClass: PropTypes.func,
  fieldClass: PropTypes.func,
  processPanelService: PropTypes.object,
};

DataTablePanel.defaultProps = {
  commentCounts: [],
  data: [],
  fields: {},
  filteredRecordCount: 0,
  tabIndex: 0,
};

export default DataTablePanel;
